<template>
  <div class="ov-page">

    <!-- Status badge -->
    <div class="ov-status-row">
      <span class="ov-badge" :class="isDisabled ? 'ov-badge--suspended' : 'ov-badge--active'">
        {{ isDisabled ? 'Suspenso' : 'Ativo' }}
      </span>
      <span class="ov-sys-username">@{{ systemUsername }}</span>
      <button class="ov-impersonate" type="button" :disabled="impersonating" title="Entrar no sistema desta obra como administrador (suporte)" @click="doImpersonate">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
        {{ impersonating ? 'Entrando...' : 'Entrar como admin' }}
      </button>
    </div>

    <!-- Key metrics grid -->
    <div class="ov-metrics">
      <div class="ov-metric">
        <span class="ov-metric-value">{{ activeUsers }}</span>
        <span class="ov-metric-label">Usuários ativos</span>
      </div>
      <div class="ov-metric">
        <span class="ov-metric-value">{{ inactiveUsers }}</span>
        <span class="ov-metric-label">Usuários inativos</span>
      </div>
      <div class="ov-metric">
        <span class="ov-metric-value">{{ activeProjects }}</span>
        <span class="ov-metric-label">Projetos ativos</span>
      </div>
      <div class="ov-metric">
        <span class="ov-metric-value">{{ totalUsers }}</span>
        <span class="ov-metric-label">Total de usuários</span>
      </div>
    </div>

    <div class="ov-grid">

      <!-- Storage card -->
      <div class="ov-card">
        <h3 class="ov-card-title">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
          Armazenamento
        </h3>
        <div class="ov-storage-bar-wrap">
          <div
            class="ov-storage-bar-fill"
            :style="{ width: storagePct + '%', background: storageColor }"
          />
        </div>
        <p class="ov-storage-label">
          <strong>{{ storageUsedLabel }}</strong>
          usados de
          <strong>{{ storageLimitLabel }}</strong>
          ({{ storagePct }}%)
        </p>
        <p class="ov-storage-warn" v-if="storagePct >= 90">⚠️ Armazenamento quase cheio</p>
      </div>

      <!-- Contract / plan card -->
      <div class="ov-card">
        <h3 class="ov-card-title">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
          Contrato
        </h3>
        <div v-if="payLoading" class="ov-loading-row">Carregando...</div>
        <div v-else class="ov-info-list">
          <div class="ov-info-row">
            <span class="ov-info-key">Plano</span>
            <span class="ov-info-val ov-plan-badge" :class="'ov-plan--' + (payment?.plan || 'basico')">{{ planLabel(payment?.plan) }}</span>
          </div>
          <div class="ov-info-row">
            <span class="ov-info-key">Status pagamento</span>
            <span class="ov-info-val">
              <span class="ov-pay-badge" :class="payStatusClass(payment?.paymentStatus)">{{ payStatusLabel(payment?.paymentStatus) }}</span>
            </span>
          </div>
          <div class="ov-info-row">
            <span class="ov-info-key">Valor contrato</span>
            <span class="ov-info-val">{{ payment?.contractValue != null ? 'R$ ' + payment.contractValue.toFixed(2) : '—' }}</span>
          </div>
          <div class="ov-info-row">
            <span class="ov-info-key">Máx. usuários</span>
            <span class="ov-info-val">{{ payment?.maxUsers ?? '—' }}</span>
          </div>
          <div class="ov-info-row">
            <span class="ov-info-key">Vencimento dia</span>
            <span class="ov-info-val">{{ payment?.paymentDay ?? '—' }}</span>
          </div>
          <div class="ov-info-row">
            <span class="ov-info-key">Último pagamento</span>
            <span class="ov-info-val">{{ payment?.lastPaymentAt ? formatDate(payment.lastPaymentAt) : '—' }}</span>
          </div>
        </div>
      </div>

      <!-- Dates card -->
      <div class="ov-card">
        <h3 class="ov-card-title">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          Datas
        </h3>
        <div class="ov-info-list">
          <div class="ov-info-row">
            <span class="ov-info-key">Início do sistema</span>
            <span class="ov-info-val">{{ createdAtLabel }}</span>
          </div>
          <div class="ov-info-row">
            <span class="ov-info-key">Renovação anual</span>
            <span class="ov-info-val">{{ renewalDateLabel }}</span>
          </div>
          <div class="ov-info-row" v-if="daysUntilRenewal !== null">
            <span class="ov-info-key">Dias até renovação</span>
            <span class="ov-info-val" :class="daysUntilRenewal <= 30 ? 'ov-warn-text' : ''">{{ daysUntilRenewal }} dias</span>
          </div>
        </div>
      </div>

      <!-- Active modules card -->
      <div class="ov-card">
        <h3 class="ov-card-title">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
          Módulos ativos
        </h3>
        <div v-if="flagsLoading" class="ov-loading-row">Carregando...</div>
        <div v-else class="ov-modules-grid">
          <div
            v-for="flag in mainFlags"
            :key="flag.key"
            class="ov-module-chip"
            :class="flag.enabled ? 'ov-module-chip--on' : 'ov-module-chip--off'"
          >
            {{ flag.label }}
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script lang="ts">
import { inject, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { masterFetch, impersonateSystem } from '@/store/masterSession'

interface Payment {
  plan: string
  maxUsers: number
  contractValue: number
  paymentDay: number
  paymentStatus: string
  lastPaymentAt: string
}

interface FeatureFlag { key: string; label: string; enabled: boolean }

export default {
  name: 'AdminSysOverviewTab',
  setup() {
    const route = useRoute()
    const detail = inject<any>('adminDetail')
    const systemId = computed(() => String(route.params.systemId || ''))

    const payment = ref<Payment | null>(null)
    const payLoading = ref(false)
    const flags = ref<FeatureFlag[]>([])
    const flagsLoading = ref(false)

    const mainFlagKeys = ['construction', 'maintenance', 'warehouse', 'ia']
    const mainFlags = computed(() => flags.value.filter(f => mainFlagKeys.includes(f.key)))

    onMounted(async () => {
      const sid = systemId.value
      payLoading.value = true
      flagsLoading.value = true
      try {
        payment.value = await masterFetch<Payment>(`/master/systems/${sid}/payment`)
      } catch { /* ignore */ } finally { payLoading.value = false }
      try {
        flags.value = await masterFetch<FeatureFlag[]>(`/master/systems/${sid}/feature-flags`)
      } catch { /* ignore */ } finally { flagsLoading.value = false }
    })

    const formatMb = (mb: number) => {
      if (!mb) return '0 MB'
      if (mb >= 1024) return (mb / 1024).toFixed(2) + ' GB'
      return mb.toFixed(1) + ' MB'
    }

    const formatDate = (d: string) => {
      if (!d) return ''
      try { return new Date(d).toLocaleDateString('pt-BR') } catch { return d }
    }

    // Flatten all detail properties into explicit computeds to avoid ref unwrapping issues
    const d = computed(() => detail?.value ?? null)

    const isDisabled    = computed(() => d.value?.disabled ?? false)
    const systemUsername = computed(() => d.value?.systemUsername ?? '')
    const activeUsers   = computed<number | string>(() => d.value?.activeUsers ?? '—')
    const inactiveUsers = computed<number | string>(() => d.value?.inactiveUsers ?? '—')
    const activeProjects = computed<number | string>(() => d.value?.activeProjects ?? '—')
    const totalUsers    = computed<number | string>(() => {
      const det = d.value
      if (!det) return '—'
      return det.activeUsers + det.inactiveUsers
    })
    const createdAtLabel   = computed(() => d.value?.createdAt ? formatDate(d.value.createdAt) : '—')
    const renewalDateLabel = computed(() => d.value?.renewalDate ? formatDate(d.value.renewalDate) : '—')
    const storageUsedLabel  = computed(() => formatMb(d.value?.usedMb ?? 0))
    const storageLimitLabel = computed(() => formatMb(d.value?.storageLimitMb ?? 0))

    const storagePct = computed(() => {
      const det = d.value
      if (!det || !det.storageLimitMb) return 0
      return Math.min(100, Math.round((det.usedMb / det.storageLimitMb) * 100))
    })

    const storageColor = computed(() => {
      const p = storagePct.value
      if (p >= 90) return '#dc2626'
      if (p >= 70) return '#f59e0b'
      return '#16a34a'
    })

    const daysUntilRenewal = computed(() => {
      const det = d.value
      if (!det?.renewalDate) return null
      const diff = new Date(det.renewalDate).getTime() - Date.now()
      return Math.ceil(diff / 86400000)
    })

    const planLabel = (plan?: string) => {
      if (plan === 'normal') return 'Normal'
      if (plan === 'completo') return 'Completo'
      return 'Básico'
    }

    const payStatusLabel = (s?: string) => {
      if (s === 'atrasado') return 'Atrasado'
      if (s === 'suspenso') return 'Suspenso'
      return 'Em dia'
    }

    const payStatusClass = (s?: string) => {
      if (s === 'atrasado') return 'ov-pay--late'
      if (s === 'suspenso') return 'ov-pay--suspended'
      return 'ov-pay--ok'
    }

    const impersonating = ref(false)
    const doImpersonate = async () => {
      const siteName = detail?.value?.siteName || 'esta obra'
      if (!window.confirm(`Entrar como administrador de "${siteName}"?\n\nVocê acessará o sistema da obra com poderes de admin. A ação fica registrada na auditoria. Sua sessão de administrador geral continua ativa — para voltar, acesse /administrador.`)) return
      impersonating.value = true
      try {
        await impersonateSystem(systemId.value)
      } catch (e) {
        impersonating.value = false
        alert(e instanceof Error ? e.message : 'Erro ao entrar como admin.')
      }
    }

    return {
      payment, payLoading, mainFlags, flagsLoading,
      isDisabled, systemUsername,
      activeUsers, inactiveUsers, activeProjects, totalUsers,
      createdAtLabel, renewalDateLabel, storageUsedLabel, storageLimitLabel,
      storagePct, storageColor, daysUntilRenewal,
      formatDate, planLabel, payStatusLabel, payStatusClass,
      impersonating, doImpersonate
    }
  }
}
</script>

<style lang="scss">
.ov-impersonate {
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 8px; cursor: pointer;
  background: #0f4c81; color: #fff; border: none;
  font-size: 12.5px; font-weight: 700;
  &:hover:not(:disabled) { background: #0d3d6a; }
  &:disabled { opacity: .6; cursor: default; }
}
.ov-page {
  padding: 24px 28px 40px;
  max-width: 900px;
}

.ov-status-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.ov-badge {
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  &--active   { background: #d1fae5; color: #065f46; }
  &--suspended { background: #fee2e2; color: #991b1b; }
}

.ov-sys-username {
  font-size: 13px;
  color: #6b7280;
  font-family: monospace;
}

.ov-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.ov-metric {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ov-metric-value {
  font-size: 26px;
  font-weight: 800;
  color: #0f4c81;
  line-height: 1;
}

.ov-metric-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.ov-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.ov-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px 20px;
}

.ov-card-title {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin: 0 0 14px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.ov-loading-row {
  font-size: 13px;
  color: #9ca3af;
}

.ov-storage-bar-wrap {
  height: 10px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 8px;
}

.ov-storage-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.4s;
}

.ov-storage-label {
  font-size: 13px;
  color: #374151;
  margin: 0 0 4px;
}

.ov-storage-warn {
  font-size: 12.5px;
  color: #dc2626;
  margin: 6px 0 0;
  font-weight: 600;
}

.ov-info-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ov-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
}

.ov-info-key {
  color: #6b7280;
  white-space: nowrap;
}

.ov-info-val {
  color: #111827;
  font-weight: 600;
  text-align: right;
}

.ov-warn-text {
  color: #f59e0b;
}

.ov-plan-badge {
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;

  &.ov-plan--basico  { background: #f3f4f6; color: #374151; }
  &.ov-plan--normal  { background: #dbeafe; color: #1e40af; }
  &.ov-plan--completo { background: #fef3c7; color: #92400e; }
}

.ov-pay-badge {
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;

  &.ov-pay--ok        { background: #d1fae5; color: #065f46; }
  &.ov-pay--late      { background: #fee2e2; color: #991b1b; }
  &.ov-pay--suspended { background: #fef3c7; color: #92400e; }
}

.ov-modules-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ov-module-chip {
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  border: 1.5px solid;

  &--on  { background: #d1fae5; color: #065f46; border-color: #6ee7b7; }
  &--off { background: #f3f4f6; color: #9ca3af; border-color: #e5e7eb; }
}
</style>
